import { takeLatest, put, call, select, all } from 'redux-saga/effects';
import {
  initialize,
  reset,
  arrayPush,
  arrayRemoveAll,
  change,
} from 'redux-form';

import * as act from 'actions';
import * as actNotif from 'actions/notifications';
import { showPageLoadingSpinner } from 'actions/loadingSpinner';
import { setFile, resetUploadImage } from 'actions/uploadImage';
import { subscriptionStatus } from 'actions/ingredients/ingredientSubscription';
import * as actItemHub from 'actions/businessLibrary/item';
import * as itemHubConstant from 'actions/businessLibrary/item/constants';
import * as actSalesType from 'actions/library/salesType';

import * as apiOutlets from 'api/outlets';
import * as apiProfile from 'api/profile';
import * as apiModifierHub from 'api/businessLibrary/modifier';
import * as apiItemHub from 'api/businessLibrary/items';
import * as apiCategoryHub from 'api/businessLibrary/category';
import * as apiSalesTypeHub from 'api/businessLibrary/salesType';
import * as apiLoyalty from 'api/customers/loyalty';
import { getCurrentInventoryDropdown } from 'api/currentInventoryDropdown';

const formatForm = {
  id: null,
  created_at: null,
  updated_at: null,
  synchronized_at: null,
  is_deleted: false,
  name: '',
  description: '',
  image: null,
  business_level_category_id: 0,
  background_color: null,
  outlet_id: null,
  guid: null,
  uniq_id: null,
  is_recipe: false,
  item_variants: [
    {
      temp_business_level_item_variant_id: 1,
      name: '',
      price: null,
      sku: '',
    },
  ],
  category: null,
  active_modifiers: null,
  is_sales_type_price: false,
  modifier_ids: [],
  remove_image: null,
};

const getItemHubState = state => state.businessLibrary.itemHub;
const getSalesTypeState = state => state.library.salesType;
const sizePage = 30;

function getPaginationOutlets(outlets, page, size) {
  if (outlets.length < sizePage) {
    return {
      outlets,
      page,
      totalPage: 1,
    };
  }

  return {
    outlets: paginate(outlets, size, page),
    page,
    totalPage: Math.ceil(outlets.length / size),
  };
}

function paginate(array, pageSize, pageNumber) {
  return array.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);
}

/** **************************************
 * API Request saga
 *************************************** */
function* fillForm(item) {
  yield put(
    initialize('search', {
      categoryId: item.categoryId,
      search: item.search,
    })
  );
}

function* fillSearch(profile) {
  let assignedOutletIds = [];
  if (profile.outlet_id.length > 1) {
    assignedOutletIds = [profile.outlet_id[0]];
  } else {
    assignedOutletIds = profile.outlet_id;
  }
  const data = {
    outletIds: assignedOutletIds,
  };
  yield put(initialize('search', data));
}

function* handleEventAsyncJob() {
  const { eventAsyncJob } = yield select(getItemHubState);
  if (
    eventAsyncJob.channelName &&
    eventAsyncJob.channelName ===
      itemHubConstant.LIBRARY_ITEM_HUB_CREATE_CHANNEL
  ) {
    yield put(actNotif.success('Item was successfully created.'));
  } else if (
    eventAsyncJob.channelName &&
    eventAsyncJob.channelName ===
      itemHubConstant.LIBRARY_ITEM_HUB_UPDATE_CHANNEL
  ) {
    yield put(actNotif.success('Item was successfully saved.'));
  } else if (
    eventAsyncJob.channelName &&
    eventAsyncJob.channelName ===
      itemHubConstant.LIBRARY_ITEM_HUB_DELETE_CHANNEL
  ) {
    yield put(actNotif.success('Item was successfully removed.'));
  }
  yield put(actItemHub.setEventAsyncJob(null));
}

export function* pageRequest() {
  yield put(showPageLoadingSpinner(true));
  yield put(act.showProgressBar());
  yield put(actItemHub.setItemDetail(null));
  yield put(subscriptionStatus());
  yield put(reset('itemHub'));
  yield put(setFile(null));
  yield put(actItemHub.setToggleWriteMode(false));
  yield put(actItemHub.setItems([]));
  yield call(handleEventAsyncJob);

  try {
    const response = yield call(apiOutlets.outlets);
    // get current dropdown
    const resCurrentInventoryDropdown = yield call(getCurrentInventoryDropdown);
    const {
      data: { current_inventory_dropdown },
    } = resCurrentInventoryDropdown;

    yield put(actItemHub.setOutlets(response.data));
    const resProfile = yield call(apiProfile.getProfile);
    yield put(
      actItemHub.setFilterState(0, resProfile.data.outlet_id[0], 0, '')
    );
    yield call(fillForm, {
      categoryId: 0,
      search: '',
    });

    const salesTypeHub = yield call(apiSalesTypeHub.getListSalesType, {
      size: 200,
      page: 0,
    });
    const salesType = salesTypeHub.data.results.map(salesType => ({
      ...salesType,
      business_level_sales_type_id: salesType.id,
    }));
    yield put(actSalesType.setListSalesType(salesType));

    resProfile.data.namespace = current_inventory_dropdown;
    yield call(fillSearch, resProfile.data);
    const params = {
      page: 0,
      categoryId: 0,
      keyword: '',
      size: 50,
    };
    yield put(actItemHub.listCategoryWithUncategorizedRequest());
    yield call(itemsRequest, params);
    const modifierHub = yield call(apiModifierHub.getModifiersList);
    yield put(actItemHub.setModifiers(modifierHub.data.results));
  } catch (error) {
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* itemsRequest(request) {
  let categoryId;
  let keyword;
  let page;
  let size;
  let previousItems;
  let items;
  if (request.payload) {
    const requestPayload = request.payload.query;
    const { filterState } = requestPayload;
    previousItems = request.payload.previousItems;
    categoryId = filterState.categoryId;
    keyword = filterState.keyword;
    page = requestPayload.page;
    size = 50;
  } else {
    categoryId = request.categoryId;
    keyword = request.keyword;
    page = request.page;
    size = request.size;
  }
  const query = {
    page,
    business_level_category_id: !parseInt(categoryId) ? '' : categoryId,
    keyword: !keyword ? '' : keyword,
    size,
  };

  try {
    const itemsResponse = yield call(getItems, query);
    if (previousItems) {
      items = itemsResponse;
      if (itemsResponse.results) {
        items.results = previousItems.concat(itemsResponse.results);
      }
    } else {
      items = itemsResponse;
    }

    if (items.results !== undefined) {
      items.results.forEach(item => {
        if (item.active_modifiers === undefined) {
          item.active_modifiers = [];
        }
        if (item.is_recipe === undefined) {
          item.is_recipe = false;
        }
        // Handle condition that attributes are removed by protobuf
        item.item_variants.forEach(itemVariant => {
          if (
            itemVariant.price === undefined &&
            (itemVariant.is_variable_price === undefined ||
              itemVariant.is_variable_price === false)
          ) {
            itemVariant.price = 0;
          }
          if (itemVariant.in_stock === undefined) {
            itemVariant.in_stock = 0;
          }
          if (itemVariant.stock_alert === undefined) {
            itemVariant.stock_alert = 0;
          }
          if (itemVariant.position === undefined) {
            itemVariant.position = 0;
          }
          if (itemVariant.add_inventory === undefined) {
            itemVariant.add_inventory = 0;
          }
          if (itemVariant.track_stock === undefined) {
            itemVariant.track_stock = false;
          }
          if (itemVariant.alert === undefined) {
            itemVariant.alert = false;
          }
          if (itemVariant.has_cogs === undefined) {
            itemVariant.has_cogs = false;
          } else if (itemVariant.cogs === undefined) {
            itemVariant.cogs = 0;
          }
          if (itemVariant.track_cogs === undefined) {
            itemVariant.track_cogs = false;
          }
          if (itemVariant.is_saved === undefined) {
            itemVariant.is_saved = false;
          }
          if (itemVariant.sales_type_item_variants !== null) {
            itemVariant.sales_type_item_variants.forEach(salesTypeItem => {
              if (
                salesTypeItem.price === undefined &&
                (salesTypeItem.is_variable_price === undefined ||
                  salesTypeItem.is_variable_price === false)
              ) {
                salesTypeItem.price = 0;
              }
            });
          } else {
            itemVariant.sales_type_item_variants = [];
          }
        });

        item.item_variants.forEach(itemVariant => {
          if (itemVariant.stock_alert && !itemVariant.in_stock) {
            itemVariant.in_stock = 0;
          }
        });
      });
      yield put(actItemHub.setItems(items.results));
    } else {
      yield put(actItemHub.setItems([]));
    }
    const meta = {
      total_items: items.meta.total_count,
      total_pages: items.meta.total_pages - 1,
      current_page: page,
    };
    yield put(actItemHub.setMetaItems(meta));
  } catch (error) {
    yield put(actNotif.error(error.message));
  } finally {
    yield put(showPageLoadingSpinner(false));
  }
}

export function* getItems(params) {
  try {
    const response = yield call(apiItemHub.getItemsHub, params);
    const {
      data: { results },
    } = response;
    const totalPages = response.data.total_pages;
    const totalCount = response.data.total_count;
    const itemsResponse = {
      results,
      meta: {
        total_pages: totalPages || 0,
        total_count: totalCount || 0,
      },
    };
    return itemsResponse;
  } catch (error) {
    // If we get an error we send Redux the appropiate action and return
    yield put(actNotif.error(error.message));
    return false;
  }
}

export function* getItemById(id) {
  const itemDetailRes = yield call(apiItemHub.getItemById, id);
  const { data: itemDetail } = itemDetailRes;
  return itemDetail;
}

export function* getLoyalty(params) {
  yield put(act.showProgressBar());

  try {
    const response = yield call(apiLoyalty.getLoyalty, params);
    return response.data;
  } catch (error) {
    // If we get an error we send Redux the appropiate action and return
    yield put(actNotif.error(error.message));
    return false;
  } finally {
    yield put(act.hideProgressBar());
    yield put(showPageLoadingSpinner(false));
  }
}

export function* onClickCreateItem() {
  const { categories, outlets, modifiers } = yield select(getItemHubState);
  const { salesType } = yield select(getSalesTypeState);
  const uncategorized = categories.find(el => el.name === 'Uncategorized');

  yield put(showPageLoadingSpinner(true));
  yield put(act.showProgressBar());
  yield put(resetUploadImage());
  const itemForm = { ...formatForm };

  itemForm.business_level_category_id = uncategorized.id;
  itemForm.item_outlets = outlets.map(outlet => {
    return {
      outlet_id: outlet.id,
      name: outlet.name,
      selected_outlet: true,
      is_custom_price: false,
      item_variant_custom_outlet_prices: [
        { temp_business_level_item_variant_id: 1, price: null },
      ],
    };
  });

  try {
    itemForm.sales_type = salesType.map(st => {
      return { ...st, selected: true };
    });
    itemForm.active_modifiers = modifiers.map(mod => {
      return { ...mod, selected: false };
    });

    yield put(initialize('itemHub', itemForm));
  } catch (e) {
    yield put(actNotif.error(e.message));
  } finally {
    yield put(showPageLoadingSpinner(false));
    yield put(actItemHub.setToggleWriteMode(true));
  }
}

export function* addOutletList() {
  const {
    outletList,
    itemDetail: { item_outlets },
  } = yield select(getItemHubState);
  yield put(act.showLoadingSpinner(true));
  const { page, totalpage } = outletList;
  const outletFormatter = yield call(
    getPaginationOutlets,
    item_outlets,
    page + 1,
    sizePage
  );
  const newOutlet = [...item_outlets, ...outletFormatter.outlets];
  yield put(
    actItemHub.setOutletListForm(newOutlet, outletFormatter.page, totalpage)
  );
  yield all(
    outletFormatter.outlets.map(outlet =>
      put(arrayPush('itemHub', 'item_outlets', outlet))
    )
  );
  yield put(act.showLoadingSpinner(false));
}

export function* onSelectItemDetail(request) {
  const {
    payload: { item },
  } = request;
  const { outlets, modifiers } = yield select(getItemHubState);
  const { salesType } = yield select(getSalesTypeState);

  yield put(showPageLoadingSpinner(true));
  yield put(reset('itemHub'));
  yield put(setFile(null));
  yield put(act.showProgressBar());
  yield put(actItemHub.setItemDetail(null));
  try {
    const itemDetail = yield call(getItemById, item.id);
    let newOutlet = [];
    if (!itemDetail.item_outlets.length) {
      newOutlet = outlets.map(outlet => {
        return {
          outlet_id: outlet.id,
          name: outlet.name,
          selected_outlet: false,
          is_edit_price: false,
          item_variant_custom_outlet_prices: itemDetail.item_variants.map(
            (itemVariant, idx) => ({
              ...itemVariant,
              id: null,
              temp_business_level_item_variant_id: idx + 1,
            })
          ),
        };
      });
    } else {
      itemDetail.item_outlets.forEach(itemOutlet => {
        if (!itemOutlet.is_custom_price) {
          itemOutlet.item_variant_custom_outlet_prices = itemDetail.item_variants.map(
            iv => {
              return {
                ...iv,
                id: null,
              };
            }
          );
        }
        itemOutlet.name = itemOutlet.outlet_name;
      });
      const newOutlets = outlets.map(outlet => {
        return {
          outlet_id: outlet.id,
          name: outlet.name,
          selected_outlet: false,
          is_custom_price: false,
          item_variant_custom_outlet_prices: itemDetail.item_variants.map(
            iv => {
              return {
                ...iv,
                business_level_item_variant_id: iv.id,
              };
            }
          ),
        };
      });
      const idsOutlet = new Set(itemDetail.item_outlets.map(d => d.outlet_id));
      newOutlet = [
        ...itemDetail.item_outlets,
        ...newOutlets.filter(outlet => !idsOutlet.has(outlet.outlet_id)),
      ];
    }
    newOutlet.sort((a, b) =>
      a.name.toLowerCase() > b.name.toLowerCase()
        ? 1
        : b.name.toLowerCase() > a.name.toLowerCase()
        ? -1
        : 0
    );
    const outletFormatter = yield call(
      getPaginationOutlets,
      newOutlet,
      1,
      sizePage
    );
    const { outlets: outletFormat, page, totalPage } = outletFormatter;
    yield put(actItemHub.setOutletListForm(outletFormat, page, totalPage));
    const ids = new Set(itemDetail.modifiers.map(mod => mod.id));
    itemDetail.item_outlets = outletFormat;
    itemDetail.active_modifiers = [
      ...itemDetail.modifiers.map(mod => ({
        ...mod,
        selected: true,
      })),
      ...modifiers
        .filter(mod => !ids.has(mod.id))
        .map(mod => ({ ...mod, selected: false })),
    ];
    itemDetail.modifier_ids = itemDetail.modifiers.map(mod => ({
      id: mod.id,
    }));
    const detail = { ...itemDetail, item_outlets: newOutlet };
    yield put(actItemHub.setItemDetail(detail));
    yield put(actSalesType.setListSalesType(salesType));

    const salesTypeIdList = salesType.map(st => st.id);

    const currentSalesType = [];
    if (itemDetail.is_sales_type_price) {
      salesType.forEach(st => {
        currentSalesType[st.id] = 0;
      });
    }
    const newSalesType = salesType.map(sales => ({
      business_level_sales_type_id: sales.business_level_sales_type_id,
      price: null,
    }));
    itemDetail.item_variants.map(function(iv) {
      if (
        iv.price === undefined &&
        (iv.is_variable_price === undefined || iv.is_variable_price === false)
      ) {
        iv.price = 0;
      }
      if (iv.sales_type_item_variants !== null) {
        iv.sales_type_item_variants.forEach(salesTypeItem => {
          if (
            salesTypeItem.price === undefined &&
            (salesTypeItem.is_variable_price === undefined ||
              salesTypeItem.is_variable_price === false)
          ) {
            salesTypeItem.price = 0;
          }
        });
      } else {
        iv.sales_type_item_variants = newSalesType;
      }
      // change price to string to handle redux form isDirty
      iv.price = iv.price || iv.price === 0 ? iv.price.toString() : iv.price;
      iv.is_editable = !iv.is_saved;
      if (itemDetail.is_sales_type_price) {
        iv.sales_type_item_variants.forEach((sti, index) => {
          if (
            salesTypeIdList.indexOf(
              iv.sales_type_item_variants[index].business_level_sales_type_id
            ) === -1
          ) {
            delete iv.sales_type_item_variants[index];
          } else {
            currentSalesType[sti.business_level_sales_type_id]++;
          }
        });
      }
      return iv;
    });

    itemDetail.sales_type = salesType.map(st => {
      return { ...st, selected: currentSalesType[st.id] > 0 };
    });
    yield put(initialize('itemHub', { ...itemDetail }));
    yield put(actItemHub.setToggleWriteMode(true));
  } catch (err) {
    yield put(actNotif.error(err.message));
    yield put(actItemHub.setToggleWriteMode(false));
    return false;
  } finally {
    yield put(act.hideProgressBar());
    yield put(showPageLoadingSpinner(false));
  }
}

export function* getCategoriesAndUncategorized() {
  try {
    const responseCategories = yield call(
      apiCategoryHub.getCategoriesWithUncategorized,
      { size: 100, with_uncategorized: true }
    );
    const categories = responseCategories.data;
    if (categories) {
      yield put(actItemHub.setCategories(categories.results));
    }
  } catch (error) {
    yield put(actNotif.error(error.message));
  }
}

export function* setFilterItemHub(request) {
  yield put(showPageLoadingSpinner(true));
  const {
    payload: { categoryId, outletId, keyword },
  } = request;

  yield put(actItemHub.setFilterState(categoryId, outletId, 0, keyword || ''));
  yield put(act.showProgressBar());
  try {
    yield call(itemsRequest, {
      page: 0,
      outletId,
      categoryId,
      typeInventoryId: 0,
      keyword,
    });
  } catch (error) {
    yield put(actNotif.error(error.message));
  } finally {
    yield put(showPageLoadingSpinner(false));
    yield put(act.hideProgressBar());
  }
}

export function* onClickBtnItemVariant(request) {
  const {
    payload: { itemForm },
  } = request;
  yield put(actItemHub.showItemVariantModal());
  yield put(initialize('modal_item_variants', itemForm));
}

export function* onCancelItemVariantModal() {
  yield put(initialize('modal_item_variants', {}));
  yield put(actItemHub.hideItemVariantModal());
}

export function* onConfirmItemVariantModal(request) {
  const { value } = request.payload;
  yield put(arrayRemoveAll('itemHub', 'item_variants'));
  yield all(
    value.item_variants.map(iv =>
      put(arrayPush('itemHub', 'item_variants', iv))
    )
  );
  yield put(change('itemHub', 'item_outlets', value.item_outlets));
  yield put(initialize('modal_item_variants', {}));
  yield put(actItemHub.hideItemVariantModal());
}

export function* onClickBtnSalesType(request) {
  const {
    payload: { itemForm },
  } = request;
  yield put(actItemHub.showSalesTypeModal());

  yield put(initialize('modal_sales_type', itemForm));
  yield put(actItemHub.setPreviousItemForm(itemForm));
}

export function* onCancelSalesTypeModal() {
  yield put(initialize('modal_item_variants', {}));
  yield put(actItemHub.hideSalesTypeModal());
}

export function* onConfirmSalesTypeModal(request) {
  const { value } = request.payload;
  yield put(arrayRemoveAll('itemHub', 'item_variants'));
  yield put(arrayRemoveAll('itemHub', 'sales_type'));

  const deselectedSalesType = value.sales_type
    .filter(salesType => !salesType.selected)
    .map(salesType => salesType.id);
  value.item_variants.forEach(itemVariant => {
    itemVariant.sales_type_item_variants = itemVariant.sales_type_item_variants.filter(
      salesTypeItem =>
        deselectedSalesType.indexOf(
          salesTypeItem.business_level_sales_type_id
        ) === -1
    );
  });

  value.item_outlets.forEach(itemOutlet => {
    itemOutlet.item_variant_custom_outlet_prices.forEach(itemVariant => {
      itemVariant.sales_type_item_variants = itemVariant.sales_type_item_variants.filter(
        salesType =>
          deselectedSalesType.indexOf(
            salesType.business_level_sales_type_id
          ) === -1
      );
    });
  });

  value.item_variants.forEach(itemVariant => {
    const existingSalesTypeIds = itemVariant.sales_type_item_variants.map(
      salesTypeItem => salesTypeItem.business_level_sales_type_id
    );
    const newSalesType = value.sales_type.filter(
      salesType =>
        existingSalesTypeIds.indexOf(salesType.business_level_sales_type_id) ===
          -1 && salesType.selected
    );
    newSalesType.forEach(salesType => {
      itemVariant.sales_type_item_variants.push({
        business_level_sales_type_id: salesType.business_level_sales_type_id,
      });
    });
  });

  value.item_outlets.forEach(itemOutlet => {
    itemOutlet.item_variant_custom_outlet_prices.forEach(itemVariant => {
      const existingSalesTypeIds = itemVariant.sales_type_item_variants.map(
        salesTypeItem => salesTypeItem.business_level_sales_type_id
      );
      const newSalesType = value.sales_type.filter(
        salesType =>
          existingSalesTypeIds.indexOf(
            salesType.business_level_sales_type_id
          ) === -1 && salesType.selected
      );
      newSalesType.forEach(salesType => {
        itemVariant.sales_type_item_variants.push({
          business_level_sales_type_id: salesType.business_level_sales_type_id,
          price: null,
        });
      });
    });
  });

  for (let idx = 0; idx < value.sales_type.length; idx++) {
    yield put(arrayPush('itemHub', 'sales_type', value.sales_type[idx]));
  }
  for (let idx = 0; idx < value.item_variants.length; idx++) {
    yield put(arrayPush('itemHub', 'item_variants', value.item_variants[idx]));
  }
  yield put(reset('modal_sales_type'));
  yield put(actItemHub.hideSalesTypeModal());
}

export function* onClickBtnModifier(request) {
  const {
    payload: { itemForm },
  } = request;
  yield put(actItemHub.showModifierModal());
  itemForm.active_modifiers.sort((a, b) => a.name.localeCompare(b.name));
  yield put(initialize('modal_modifier', itemForm));
}

export function* onCancelModifierModal() {
  yield put(initialize('modal_modifier', {}));
  yield put(actItemHub.hideModifierModal());
}

export function* onConfirmModifierModal(request) {
  const { value } = request.payload;
  yield put(arrayRemoveAll('itemHub', 'active_modifiers'));
  yield put(arrayRemoveAll('itemHub', 'modifier_ids'));

  yield all(
    value.active_modifiers.map(mod =>
      put(arrayPush('itemHub', 'active_modifiers', mod))
    )
  );
  yield all(
    value.active_modifiers.map(mod => {
      if (mod.selected) {
        return put(arrayPush('itemHub', 'modifier_ids', { id: mod.id }));
      }
    })
  );
  yield put(initialize('modal_modifier', {}));
  yield put(actItemHub.hideModifierModal());
}

export function* onClickCancel() {
  yield put(actItemHub.setItemDetail(null));
  yield put(actItemHub.setToggleWriteMode(false));
  yield put(reset('itemHub'));
  yield put(setFile(null));
}

const mappingCustomItemVariant = (outletPrices, isSalesTyperice) => {
  const value = {};
  if (!isSalesTyperice) {
    outletPrices.item_variant_custom_outlet_prices.forEach(itemVariant => {
      delete itemVariant.sales_type_item_variants;
    });
  }
  if (outletPrices.is_custom_price) {
    value.item_variant_custom_outlet_prices =
      outletPrices.item_variant_custom_outlet_prices;
  }
  value.outlet_id = outletPrices.outlet_id;
  value.name = outletPrices.name;
  value.is_custom_price = outletPrices.is_custom_price;
  return value;
};

const mappingItemVariants = (itemVariant, isSalesTypePrice) => {
  if (!isSalesTypePrice) {
    delete itemVariant.sales_type_item_variants;
  } else {
    itemVariant.sales_type_item_variants.map(salesType => {
      if (!salesType.price) {
        salesType.price = null;
      }
    });
  }
  return itemVariant;
};

const readAsText = file => {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = event => resolve(event.target.result);
  });
};

export function* onSubmitFormCreate(request) {
  const {
    payload: { values },
  } = request;
  values.file_image = {
    data: '',
    file_name: '',
  };
  if (values.file !== null) {
    values.file_image.data = yield call(readAsText, values.file);
    values.file_image.file_name = values.file.name;
  }

  const formData = {
    id: values.id,
    name: values.name,
    description: values.description,
    business_level_category_id: values.business_level_category_id,
    is_sales_type_price: values.is_sales_type_price,
    item_variants: values.item_variants.map(itemVariant =>
      mappingItemVariants(itemVariant, values.is_sales_type_price)
    ),
    file_image: values.file_image,
    modifiers: values.modifier_ids,
    item_outlets: values.item_outlets
      .filter(outlet => outlet.selected_outlet)
      .map(itemVariant =>
        mappingCustomItemVariant(itemVariant, values.is_sales_type_price)
      ),
  };
  yield put(actItemHub.hideConfirmItemModal());
  yield put(actItemHub.setItemDetail(values));
  yield put(act.showProgressBar());
  yield put(showPageLoadingSpinner(true));
  try {
    yield put(
      actItemHub.setEventAsyncJob(
        itemHubConstant.LIBRARY_ITEM_HUB_CREATE_CHANNEL
      )
    );
    yield call(apiItemHub.postItemHub, formData);
  } catch (error) {
    yield put(initialize('itemHub', values));
    yield put(showPageLoadingSpinner(false));
    yield put(actItemHub.setItemDetail(null));
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* onSubmitFormUpdate(request) {
  const {
    payload: { values },
  } = request;
  const {
    outletList: { page, totalpage },
    itemDetail,
  } = yield select(getItemHubState);
  values.file_image = {
    data: '',
    file_name: '',
  };
  if (page < totalpage) {
    const startIndex = page * sizePage;
    for (let i = startIndex; i < itemDetail.item_outlets.length; i++) {
      values.item_outlets.push(itemDetail.item_outlets[i]);
    }
  }
  if (values.file !== null) {
    values.file_image.data = yield call(readAsText, values.file);
    values.file_image.file_name = values.file.name;
  }

  const formData = {
    id: values.id,
    name: values.name,
    description: values.description,
    business_level_category_id: values.business_level_category_id,
    is_sales_type_price: values.is_sales_type_price,
    item_variants: values.item_variants.map(itemVariant =>
      mappingItemVariants(itemVariant, values.is_sales_type_price)
    ),
    file_image: values.file_image,
    modifiers: values.modifier_ids,
    item_outlets: values.item_outlets
      .filter(outlet => outlet.selected_outlet)
      .map(itemVariant =>
        mappingCustomItemVariant(itemVariant, values.is_sales_type_price)
      ),
  };
  yield put(actItemHub.hideConfirmItemModal());
  yield put(actItemHub.setItemDetail(values));
  yield put(act.showProgressBar());
  yield put(showPageLoadingSpinner(true));
  try {
    yield put(
      actItemHub.setEventAsyncJob(
        itemHubConstant.LIBRARY_ITEM_HUB_UPDATE_CHANNEL
      )
    );
    yield call(apiItemHub.putItemById, values.id, formData);
  } catch (error) {
    yield put(initialize('itemHub', values));
    yield put(showPageLoadingSpinner(false));
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* onDeleteItem(request) {
  const {
    payload: { values },
  } = request;
  yield put(actItemHub.hideDeleteModal());
  yield put(act.showProgressBar());
  yield put(showPageLoadingSpinner(true));
  try {
    yield put(
      actItemHub.setEventAsyncJob(
        itemHubConstant.LIBRARY_ITEM_HUB_DELETE_CHANNEL
      )
    );
    yield call(apiItemHub.deleteItemById, values.id);
  } catch (error) {
    yield put(initialize('itemHub', values));
    yield put(actNotif.error(error.message));
    yield put(showPageLoadingSpinner(false));
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* handleErrorAsyncJob(request) {
  const {
    payload: { error },
  } = request;
  const { itemDetail } = yield select(getItemHubState);
  yield put(actNotif.error(error));
  yield put(initialize('itemHub', itemDetail));
  yield put(showPageLoadingSpinner(false));
}

export function* handleCheckedSalesType(request) {
  const {
    payload: { event, formValue },
  } = request;
  if (event) {
    const newSalesType = formValue.sales_type.map(sales => ({
      business_level_sales_type_id: sales.business_level_sales_type_id,
      price: null,
    }));
    formValue.item_variants.map(itemVariant => {
      if (
        !itemVariant.sales_type_item_variants ||
        itemVariant.sales_type_item_variants.length === 0
      ) {
        itemVariant.sales_type_item_variants = newSalesType;
      } else {
        const itemVariantSalesTypeIdList = itemVariant.sales_type_item_variants.map(
          sti => sti.business_level_sales_type_id
        );
        formValue.sales_type.forEach(salesType => {
          if (
            itemVariantSalesTypeIdList.indexOf(
              salesType.business_level_sales_type_id
            ) > -1
          ) {
            salesType.selected = true;
          }
        });
      }
    });

    formValue.item_outlets.map(itemOutlet => {
      itemOutlet.item_variant_custom_outlet_prices.forEach(iv => {
        if (!iv.sales_type_item_variants) {
          iv.sales_type_item_variants = newSalesType;
        }
      });
    });

    yield put(change('itemHub', 'sales_type', formValue.sales_type));
    yield put(change('itemHub', 'item_variants', formValue.item_variants));
  }
}

export default function* libraryItemHubSaga() {
  yield takeLatest(itemHubConstant.LIBRARY_ITEM_HUB_PAGE_REQUEST, pageRequest);
  yield takeLatest(
    itemHubConstant.LIBRARY_ITEM_HUB_ON_SELECT_ITEM,
    onSelectItemDetail
  );
  yield takeLatest(
    itemHubConstant.LIBRARY_ITEM_HUB_LIST_CATEGORY_WITH_UNCATEGORIZED_REQUEST,
    getCategoriesAndUncategorized
  );
  yield takeLatest(
    itemHubConstant.LIBRARY_ITEM_HUB_ON_CLICK_CREATE_ITEM,
    onClickCreateItem
  );
  yield takeLatest(
    itemHubConstant.LIBRARY_ITEM_HUB_SET_FILTER_ITEM,
    setFilterItemHub
  );
  yield takeLatest(
    itemHubConstant.LIBRARY_ITEM_HUB_ON_CLICK_BTN_ITEM_VARIANT,
    onClickBtnItemVariant
  );
  yield takeLatest(
    itemHubConstant.LIBRARY_ITEM_HUB_ON_CLICK_BTN_MODIFIER,
    onClickBtnModifier
  );
  yield takeLatest(
    itemHubConstant.LIBRARY_ITEM_HUB_ON_CANCEL_ITEM_VARIANT_MODAL,
    onCancelItemVariantModal
  );
  yield takeLatest(
    itemHubConstant.LIBRARY_ITEM_HUB_ON_CONFIRM_ITEM_VARIANT_MODAL,
    onConfirmItemVariantModal
  );
  yield takeLatest(
    itemHubConstant.LIBRARY_ITEM_HUB_ON_CLICK_BTN_SALES_TYPE,
    onClickBtnSalesType
  );
  yield takeLatest(
    itemHubConstant.LIBRARY_ITEM_HUB_ON_CANCEL_SALES_TYPE_MODAL,
    onCancelSalesTypeModal
  );
  yield takeLatest(
    itemHubConstant.LIBRARY_ITEM_HUB_ON_CONFIRM_SALES_TYPE_MODAL,
    onConfirmSalesTypeModal
  );
  yield takeLatest(
    itemHubConstant.LIBRARY_ITEM_HUB_ON_CANCEL_MODIFIER_MODAL,
    onCancelModifierModal
  );
  yield takeLatest(
    itemHubConstant.LIBRARY_ITEM_HUB_ON_CONFIRM_MODIFIER_MODAL,
    onConfirmModifierModal
  );
  yield takeLatest(
    itemHubConstant.LIBRARY_ITEM_HUB_ON_CLICK_CANCEL,
    onClickCancel
  );
  yield takeLatest(
    itemHubConstant.LIBRARY_ITEM_HUB_SUBMIT_FORM_CREATE,
    onSubmitFormCreate
  );
  yield takeLatest(
    itemHubConstant.LIBRARY_ITEM_HUB_SUBMIT_FORM_UPDATE,
    onSubmitFormUpdate
  );
  yield takeLatest(
    itemHubConstant.LIBRARY_ITEM_HUB_ON_DELETE_ITEM,
    onDeleteItem
  );
  yield takeLatest(
    itemHubConstant.LIBRARY_ITEM_HUB_HANDLE_ERROR_ASYNC_JOB,
    handleErrorAsyncJob
  );
  yield takeLatest(
    itemHubConstant.LIBRARY_ITEM_HUB_ON_CHECKED_SALES_TYPE,
    handleCheckedSalesType
  );
  yield takeLatest(
    itemHubConstant.LIBRARY_ITEM_HUB_ADD_OUTLET_LIST,
    addOutletList
  );
  yield takeLatest(itemHubConstant.LIBRARY_ITEM_HUB_ITEM_REQUEST, itemsRequest);
}
